
import { useState, useEffect } from 'react';
import { useAuthSession } from './use-auth-session';
import { supabase, logoutUser } from '@/lib/supabase';
import { useToast } from './use-toast';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';

export const useAuth = () => {
  const session = useAuthSession();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isHost, setIsHost] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [userId, setUserId] = useState<string | null>(null);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  useEffect(() => {
    const checkUserStatus = async (userId: string) => {
      try {
        // Log user metadata for debugging
        console.log('User metadata:', session?.user.user_metadata);
        console.log('Checking status for user:', userId);
        
        // Check for admin status
        const { data: adminData, error: adminError } = await supabase
          .from('admin_users')
          .select('*')
          .eq('id', userId)
          .single();
          
        if (!adminError && adminData) {
          console.log('✅ User is admin based on admin_users table');
          setIsAdmin(true);
        } else {
          console.log('❌ User is not an admin');
          setIsAdmin(false);
        }
        
        // Check host status
        // First check user metadata (role field - highest priority)
        if (session?.user.user_metadata?.role === 'host') {
          console.log('✅ User is host based on metadata (primary check)');
          setIsHost(true);
          return;
        }

        // Second, check the profiles table for host status or role
        const { data: profileData, error: profileError } = await supabase
          .from('profiles')
          .select('role')
          .eq('id', userId)
          .single();
        
        if (profileError) {
          console.error('Error checking profile host status:', profileError);
        } else if (profileData) {
          console.log('Profile data:', profileData);
          
          // Check if role is 'host'
          if (profileData.role === 'host') {
            console.log('✅ User is host based on profiles table');
            setIsHost(true);
            return;
          }
        }
        
        // If we got here, user is not a host
        console.log('❌ User is not a host (based on all checks)');
        setIsHost(false);
      } catch (err) {
        console.error('Error determining user status:', err);
        setIsHost(false);
        setIsAdmin(false);
      }
    };

    if (session) {
      console.log('Session found:', session.user.id);
      setIsAuthenticated(true);
      setUserId(session.user.id);
      
      // Check user roles
      checkUserStatus(session.user.id);
    } else {
      console.log('No session found, user is not authenticated');
      setIsAuthenticated(false);
      setIsHost(false);
      setIsAdmin(false);
      setUserId(null);
    }
  }, [session]);

  const handleLogout = async () => {
    try {
      // Prevent duplicate logout attempts
      if (isLoggingOut) {
        console.log('Logout already in progress, skipping...');
        return;
      }
      
      console.log('Logout initiated...');
      setIsLoggingOut(true);
      
      // Immediately update UI state to improve perceived performance
      setIsAuthenticated(false);
      setIsHost(false);
      setIsAdmin(false);
      setUserId(null);
      
      // Notify user that logout is in progress
      toast({
        title: "Signing out...",
        description: "Please wait while we log you out.",
      });
      
      // First invalidate any cached queries to avoid stale data
      queryClient.removeQueries();
      queryClient.clear();
      
      // Use our enhanced logoutUser helper with multiple fallback strategies
      const result = await logoutUser();
      
      console.log('Logout result:', result);
      
      // Force invalidate the session query
      queryClient.invalidateQueries({ queryKey: ['session'] });
      
      // Notify user of success regardless of method used
      toast({
        title: "Signed Out",
        description: "You've been successfully signed out.",
      });
      
      // Navigate to home page with replace to prevent back navigation to protected routes
      navigate('/', { replace: true });
      
      // Force a page reload as absolute last resort if we're still on the same domain
      // This ensures we completely reset the app state
      setTimeout(() => {
        if (window.location.pathname !== '/') {
          console.log('Forcing page reload to ensure clean slate');
          window.location.href = '/';
        }
      }, 500);
      
    } catch (error) {
      console.error('Error in handleLogout:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "There was a problem during logout. Please refresh the page.",
      });
      
      // Still invalidate queries and clear state even on error
      queryClient.invalidateQueries({ queryKey: ['session'] });
      queryClient.clear();
      
      // Force navigation anyway
      navigate('/', { replace: true });
    } finally {
      setIsLoggingOut(false);
    }
  };

  return {
    isAuthenticated,
    isHost,
    isAdmin,
    userId,
    session,
    isLoggingOut,
    onLogout: handleLogout,
  };
};
